/**
 * Route Prefetching for Instant Navigation
 * 
 * Prefetches data for likely next routes based on user context,
 * reducing perceived latency when navigating.
 */

import { QueryClient } from '@tanstack/react-query';

export interface PrefetchConfig {
  /** Query key to prefetch */
  queryKey: readonly unknown[];
  /** Function to fetch the data */
  queryFn: () => Promise<unknown>;
  /** Stale time for this prefetch (ms) */
  staleTime?: number;
}

/**
 * Routes and their likely next destinations
 * Used to prefetch data proactively
 */
const ROUTE_PREFETCH_MAP: Record<string, string[]> = {
  '/': ['/score', '/daily-log', '/network'],
  '/auth': ['/'],
  '/score': ['/', '/analytics'],
  '/daily-log': ['/', '/mortality-log', '/feed-log'],
  '/network': ['/', '/knowledge'],
  '/profile': ['/settings'],
};

/**
 * Prefetch data for a specific route
 */
export async function prefetchForRoute(
  queryClient: QueryClient,
  route: string,
  configs: PrefetchConfig[]
): Promise<void> {
  const prefetchPromises = configs.map(async (config) => {
    try {
      await queryClient.prefetchQuery({
        queryKey: config.queryKey,
        queryFn: config.queryFn,
        staleTime: config.staleTime ?? 1000 * 60 * 5, // 5 min default
      });
    } catch (error) {
      // Silently fail prefetches - they're optimistic
      console.debug(`Prefetch failed for ${config.queryKey}:`, error);
    }
  });

  await Promise.allSettled(prefetchPromises);
}

/**
 * Get likely next routes from current route
 */
export function getLikelyNextRoutes(currentRoute: string): string[] {
  return ROUTE_PREFETCH_MAP[currentRoute] ?? [];
}

/**
 * Hook into route changes to prefetch data
 * Call this when navigation occurs
 */
export function onRouteChange(
  queryClient: QueryClient,
  newRoute: string,
  getPrefetchConfigs: (route: string) => PrefetchConfig[]
): void {
  const likelyRoutes = getLikelyNextRoutes(newRoute);
  
  // Prefetch in background after a short delay
  setTimeout(() => {
    likelyRoutes.forEach((route) => {
      const configs = getPrefetchConfigs(route);
      if (configs.length > 0) {
        prefetchForRoute(queryClient, route, configs);
      }
    });
  }, 500); // Delay to not block main thread
}

/**
 * Prefetch critical data on app load
 * Called once during initialization
 */
export async function prefetchCriticalData(
  queryClient: QueryClient,
  configs: PrefetchConfig[]
): Promise<void> {
  console.log('🔮 Prefetching critical data...');
  await prefetchForRoute(queryClient, 'initial', configs);
  console.log('🔮 Critical data prefetched');
}

/**
 * Invalidate prefetched data for a route
 * Call when data becomes stale
 */
export function invalidatePrefetchedRoute(
  queryClient: QueryClient,
  route: string,
  queryKeys: readonly unknown[][]
): void {
  queryKeys.forEach((key) => {
    queryClient.invalidateQueries({ queryKey: key });
  });
}
